import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Download, Search, RefreshCw, Users } from 'lucide-react';
import { getIncentiveLeads, type IncentiveDetailRequest } from '@/services/reportsService';
import { downloadCSV, type CsvColumn } from '@/utils/csv';
import { toastMessage } from '@/utils/toast-message';

interface LeadRow {
  lead_id: number;
  agent_name: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  lead_bucket: string | null;
  owner_name: string | null;
  last_remark: string | null;
  last_remark_at: string | null;
  created_date: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  request: IncentiveDetailRequest;
}

const BUCKET_LABEL: Record<string, string> = {
  abandoned_cart: 'Abandoned Cart',
  reorder: 'Reorder',
  rto: 'RTO',
};

const fmtDate = (s: string | null) => (s ? s.split('T')[0] : '—');

const IncentiveLeadsModal = ({ isOpen, onClose, title, request }: Props) => {
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIncentiveLeads(request);
      const r = res as { success?: boolean; data?: LeadRow[] } | null;
      if (r?.success) setRows((r.data as LeadRow[]) || []);
      else toastMessage('Failed to load leads', 'error');
    } catch {
      toastMessage('Error loading leads', 'error');
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      fetchData();
    }
  }, [isOpen, fetchData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.phone || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.agent_name || '').toLowerCase().includes(q) ||
      (r.last_remark || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const handleExport = () => {
    if (filtered.length === 0) {
      toastMessage('Nothing to export', 'error');
      return;
    }
    const cols: CsvColumn<LeadRow>[] = [
      { header: 'Name', accessor: r => r.name || '' },
      { header: 'Phone', accessor: r => r.phone || '' },
      { header: 'Email', accessor: r => r.email || '' },
      { header: 'Bucket', accessor: r => (r.lead_bucket ? (BUCKET_LABEL[r.lead_bucket] || r.lead_bucket) : 'No Bucket') },
      { header: 'Agent', accessor: r => r.agent_name || '' },
      { header: 'Owner', accessor: r => r.owner_name || '' },
      { header: 'Last Remark', accessor: r => r.last_remark || '' },
      { header: 'Last Remark At', accessor: r => fmtDate(r.last_remark_at) },
      { header: 'Created Date', accessor: r => fmtDate(r.created_date) },
    ];
    const stamp = `${request.from_date}_to_${request.to_date}`;
    downloadCSV(`incentive_leads_${stamp}`, cols, filtered);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-rose-500" />
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {loading ? 'Loading…' : `${filtered.length} of ${rows.length} leads`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, phone, agent, remark…"
              className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg pl-8 pr-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
            />
          </div>
          <button
            onClick={handleExport}
            disabled={loading || filtered.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <RefreshCw className="h-8 w-8 mb-3 animate-spin opacity-50" />
              <p className="text-sm">Loading leads…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">No leads found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/80 backdrop-blur">
                <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-2.5 font-semibold">Name</th>
                  <th className="px-4 py-2.5 font-semibold">Phone</th>
                  <th className="px-4 py-2.5 font-semibold">Bucket</th>
                  <th className="px-4 py-2.5 font-semibold">Agent</th>
                  <th className="px-4 py-2.5 font-semibold">Last Remark</th>
                  <th className="px-4 py-2.5 font-semibold">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.map(r => (
                  <tr key={r.lead_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-2.5">
                      <div className="text-gray-900 dark:text-white">{r.name || '—'}</div>
                      <div className="text-xs text-gray-400">{r.email || ''}</div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-700 dark:text-gray-300">{r.phone || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                      {r.lead_bucket ? (BUCKET_LABEL[r.lead_bucket] || r.lead_bucket) : <span className="text-gray-400">No Bucket</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{r.agent_name || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 max-w-xs truncate" title={r.last_remark || ''}>{r.last_remark || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{fmtDate(r.created_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncentiveLeadsModal;
